import type { Client, Form, FormSchema, Submission, AuditEntry, FailedSubmission, Site, SiteDomain, HostProvider, SuspensionResult } from './types.js';

const API_ORIGIN = 'https://forms.conversionhive.au';
const BASE = `${API_ORIGIN}/api/admin`;

async function req<T>(
  path: string,
  opts?: RequestInit,
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...((opts?.headers as Record<string, string>) ?? {}) },
    ...opts,
  });
  const json = (await res.json()) as { ok: boolean; error?: string } & Record<string, unknown>;
  if (!json.ok) throw new Error(json.error ?? `Request failed (${res.status})`);
  return json as T;
}

// ── Clients ────────────────────────────────────────────────
export async function listClients(page = 1, search = ''): Promise<{ clients: Client[] }> {
  const params = new URLSearchParams({ page: String(page) });
  if (search) params.set('search', search);
  return req(`/clients?${params}`);
}

export async function getClient(id: string): Promise<{ client: Client }> {
  return req(`/clients/${id}`);
}

export async function createClient(data: Partial<Client> & { form_schema: FormSchema }): Promise<{ client: Client }> {
  return req('/clients', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateClient(id: string, data: Partial<Client>): Promise<{ client: Client }> {
  return req(`/clients/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export async function archiveClient(id: string): Promise<void> {
  await req(`/clients/${id}`, { method: 'DELETE' });
}

export async function suspendClient(id: string): Promise<SuspensionResult> {
  const res = await req<{ result: SuspensionResult }>(`/clients/${id}/suspend`, { method: 'POST' });
  return res.result;
}

export async function resumeClient(id: string): Promise<SuspensionResult> {
  const res = await req<{ result: SuspensionResult }>(`/clients/${id}/resume`, { method: 'POST' });
  return res.result;
}

// ── Submissions ────────────────────────────────────────────
export async function listSubmissions(
  clientId: string,
  opts: { page?: number; status?: string | undefined; form_id?: string | undefined; date_from?: number; date_to?: number } = {},
): Promise<{ submissions: Submission[] }> {
  const params = new URLSearchParams({ page: String(opts.page ?? 1) });
  if (opts.status) params.set('status', opts.status);
  if (opts.form_id) params.set('form_id', opts.form_id);
  if (opts.date_from) params.set('date_from', String(opts.date_from));
  if (opts.date_to) params.set('date_to', String(opts.date_to));
  return req(`/submissions/client/${clientId}?${params}`);
}

export async function getSubmission(id: string): Promise<{ submission: Submission }> {
  return req(`/submissions/${id}`);
}

export async function deleteSubmission(id: string): Promise<void> {
  await req(`/submissions/${id}`, { method: 'DELETE' });
}

export async function resendEmail(id: string): Promise<void> {
  await req(`/submissions/${id}/resend-email`, { method: 'POST' });
}

export async function retriggerWebhook(id: string): Promise<void> {
  await req(`/submissions/${id}/retrigger-webhook`, { method: 'POST' });
}

export function exportCsvUrl(clientId: string): string {
  return `${BASE}/submissions/client/${clientId}/export`;
}

// ── Audit ──────────────────────────────────────────────────
export interface SenderProfileOption {
  value: 'default' | 'alternate';
  email: string;
}

export async function getConfig(): Promise<{
  turnstile_site_key: string;
  recaptcha_site_key?: string;
  sender_profiles?: SenderProfileOption[];
}> {
  return req('/config');
}

export interface PresetCatalogItem {
  key: string;
  name: string;
  requiresId: boolean;
  idLabel?: string;
  idPlaceholder?: string;
}
export async function getPresetCatalog(): Promise<{ presets: PresetCatalogItem[] }> {
  return req('/presets');
}

export async function listFailures(opts: {
  page?: number;
  client_id?: string | undefined;
  reason?: string | undefined;
} = {}): Promise<{ failures: FailedSubmission[] }> {
  const params = new URLSearchParams({ page: String(opts.page ?? 1) });
  if (opts.client_id) params.set('client_id', opts.client_id);
  if (opts.reason) params.set('reason', opts.reason);
  return req(`/failures?${params}`);
}

export async function getFailure(id: string): Promise<{ failure: FailedSubmission }> {
  return req(`/failures/${id}`);
}

export async function listAudit(opts: {
  page?: number;
  actor?: string | undefined;
  action?: string | undefined;
  date_from?: number;
  date_to?: number;
} = {}): Promise<{ entries: AuditEntry[] }> {
  const params = new URLSearchParams({ page: String(opts.page ?? 1) });
  if (opts.actor) params.set('actor', opts.actor);
  if (opts.action) params.set('action', opts.action);
  if (opts.date_from) params.set('date_from', String(opts.date_from));
  if (opts.date_to) params.set('date_to', String(opts.date_to));
  return req(`/audit?${params}`);
}

// ── Forms ──────────────────────────────────────────────────
export const formsApi = {
  list: (clientId?: string): Promise<{ ok: true; forms: Form[] }> =>
    req(`/forms${clientId ? `?client_id=${encodeURIComponent(clientId)}` : ''}`),
  /** `now` is the server's clock — used to render the test-mode countdown without trusting the browser's. */
  get: (id: string): Promise<{ ok: true; form: Form; now?: number }> =>
    req(`/forms/${id}`),
  create: (body: {
    client_id: string;
    name: string;
    form_schema: FormSchema;
    notify_emails?: string[];
    email_enabled?: boolean;
    webhook_url?: string;
    webhook_secret?: string;
    turnstile_enabled?: boolean;
    is_primary?: boolean;
    captcha_provider?: 'none' | 'turnstile' | 'recaptcha';
    enhanced_conversions_enabled?: boolean;
  }): Promise<{ ok: true; form: Form }> =>
    req('/forms', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, patch: Partial<{
    name: string;
    form_schema: FormSchema;
    notify_emails: string[];
    email_enabled: boolean;
    webhook_url: string;
    webhook_secret: string;
    turnstile_enabled: boolean;
    status: 'active' | 'paused' | 'archived';
    is_primary: boolean;
    captcha_provider: 'none' | 'turnstile' | 'recaptcha';
    enhanced_conversions_enabled: boolean;
  }>): Promise<{ ok: true; form: Form }> =>
    req(`/forms/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  delete: (id: string): Promise<void> =>
    req(`/forms/${id}`, { method: 'DELETE' }),
  /**
   * Arm test mode for a fixed 300s (the worker sets the duration, not us).
   * Submissions in the window are stored and flagged, but the client is neither
   * emailed nor webhooked.
   */
  enableTestMode: (id: string): Promise<{ ok: true; test_mode_until: number; now?: number }> =>
    req(`/forms/${id}/test-mode`, { method: 'POST', body: JSON.stringify({}) }),
  disableTestMode: (id: string): Promise<{ ok: true }> =>
    req(`/forms/${id}/test-mode`, { method: 'DELETE' }),
  /**
   * Send one test notification through this client's real email pipeline.
   * Deliberately takes no recipient — the worker addresses it to the logged-in
   * user's own email, so a client can never be emailed by mistake.
   */
  sendTestEmail: (id: string): Promise<{ ok: true; recipient: string; sender: string }> =>
    req(`/forms/${id}/test-email`, { method: 'POST' }),
};

/** The signed-in dashboard user's email — the only address a test email can go to. */
export async function getMe(): Promise<string | null> {
  try {
    const res = await fetch(`${API_ORIGIN}/auth/me`, { credentials: 'include' });
    if (!res.ok) return null;
    const data = await res.json() as { ok: boolean; email?: string };
    return data.ok ? (data.email ?? null) : null;
  } catch {
    return null;
  }
}

// ── Sites ──────────────────────────────────────────────────
export const sitesApi = {
  /** `providers` lists the hosts this worker actually has credentials for. */
  list: (): Promise<{ ok: true; sites: Site[]; providers?: HostProvider[] }> =>
    req('/sites'),
  get: (id: string): Promise<{ ok: true; site: Site; file_paths: string[]; domains: SiteDomain[] }> =>
    req(`/sites/${id}`),
  delete: (id: string): Promise<void> =>
    req(`/sites/${id}`, { method: 'DELETE' }),
  verifyDomain: (hostname: string): Promise<{ ok: true; domain: SiteDomain }> =>
    req(`/sites/domains/${encodeURIComponent(hostname)}/verify`, { method: 'POST', body: JSON.stringify({}) }),
  removeDomain: (hostname: string): Promise<void> =>
    req(`/sites/domains/${encodeURIComponent(hostname)}`, { method: 'DELETE' }),
  create: (body: {
    slug: string;
    name: string;
    client_id?: string;
    provider?: HostProvider;
    files?: Array<{ path: string; data: string; encoding: 'utf-8' | 'base64' }>;
  }) => req<{ ok: true; site: Site; default_url: string }>('/sites', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  addDomain: (siteId: string, hostname: string) =>
    req<{ ok: true; domain: SiteDomain }>(`/sites/${siteId}/domains`, {
      method: 'POST',
      body: JSON.stringify({ hostname }),
    }),
  replaceFile: (
    siteId: string,
    body: { path: string; data: string; encoding: 'utf-8' | 'base64' },
  ) => req<{ ok: true; deployment_id: string; deployment_url: string }>(
    `/sites/${siteId}/files/replace`,
    { method: 'POST', body: JSON.stringify(body) },
  ),
};
